import { CopyButton, Panel } from "../../components/ui";
import { developerApi, gameApi } from "../../lib/api";
import { useQuery } from "../../lib/query";
import { Link } from "../../lib/router";
import { useClient, useSession } from "../../lib/session";
import type { GameTabProps } from "./Game";

export function GameOverview({ game }: GameTabProps) {
  const client = useClient();
  const { session } = useSession();
  const dev = developerApi(client);
  const sb = gameApi(client, { gameId: game.id, env: "sandbox" });
  const banks = useQuery(`dev:banks:${game.id}:sandbox`, () => sb.banks());
  const keys = useQuery(`dev:keys:${game.id}`, () => dev.keys(game.id));
  const audit = useQuery(`dev:audit:${game.id}:sandbox:money`, () => dev.auditLogs(game.id, { environment: "sandbox", limit: 200 }));

  const activeKeys = (keys.data?.data ?? []).filter((k) => !k.revoked_at);
  const steps = [
    {
      done: (banks.data?.data.length ?? 0) > 0,
      title: "Create a sandbox bank",
      body: "Pick a name, a currency and a brand colour. The bank gets its own account numbers and books.",
      to: `/dev/games/${game.id}/banks`,
      cta: "Create a bank",
    },
    {
      done: activeKeys.some((k) => k.environment === "sandbox" && !k.label.startsWith("Sandbox playground")),
      title: "Issue a sandbox key for your game server",
      body: "Your server uses it to open accounts and move money. Keep it out of the game client.",
      to: `/dev/games/${game.id}/keys`,
      cta: "Issue a key",
    },
    {
      done: (audit.data?.data ?? []).some((a) => a.action === "transfer.posted" || a.action === "funding.posted"),
      title: "Move some money in the playground",
      body: "Open two accounts, pay a salary, send a transfer and watch the statements update.",
      to: `/dev/games/${game.id}/sandbox`,
      cta: "Open the playground",
    },
    {
      done: activeKeys.some((k) => k.environment === "production"),
      title: "Go live with production keys",
      body: "Create your production bank and keys the same way. Sandbox data never mixes with production.",
      to: `/dev/games/${game.id}/keys?env=production`,
      cta: "Set up production",
    },
  ];
  const next = steps.findIndex((s) => !s.done);
  const base = session?.baseUrl || window.location.origin;

  return (
    <div className="stack">
      <Panel title="Getting started" sub={next === -1 ? "All set. Your game is live." : `${next} of ${steps.length} steps done.`} flush>
        <div className="progress" aria-hidden="true">
          <i style={{ width: `${((next === -1 ? steps.length : next) / steps.length) * 100}%` }} />
        </div>
        <ol className="steps">
          {steps.map((s, i) => (
            <li key={s.title} className="step" data-state={s.done ? "done" : i === next ? "next" : "todo"}>
              <span className="step-dot" aria-hidden="true">
                {s.done ? "✓" : i + 1}
              </span>
              <div className="grow">
                <div className="step-title">
                  {s.title}
                  <span className="sr-only">{s.done ? " (done)" : ""}</span>
                </div>
                <p className="muted small">{s.body}</p>
              </div>
              {!s.done && (
                <Link className={`btn small ${i === next ? "primary" : ""}`} to={s.to}>{s.cta}</Link>
              )}
            </li>
          ))}
        </ol>
      </Panel>
      <div className="grid-2">
        <Panel title="Reference">
          <dl className="facts">
            <dt>Game ID</dt>
            <dd><span className="mono">{game.id}</span> <CopyButton text={game.id} /></dd>
            <dt>Slug</dt>
            <dd className="mono">{game.slug}</dd>
            <dt>API address</dt>
            <dd className="mono">{base}/v1</dd>
            <dt>API reference</dt>
            <dd><a href={`${session?.baseUrl ?? ""}/v1/openapi.yaml`} target="_blank" rel="noreferrer">OpenAPI document</a></dd>
          </dl>
        </Panel>
        <Panel title="First call from your server">
          <pre className="json" style={{ maxHeight: "none" }}>{`curl ${base}/v1/players \\
  -H "Authorization: Bearer $GBE_SANDBOX_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"external_player_id":"player-42","display_name":"Ada Obi"}'`}</pre>
          <p className="small muted" style={{ marginTop: 8 }}>
            Money-moving calls also need an <code>Idempotency-Key</code> header: a unique ID per operation, reused when you retry.
          </p>
        </Panel>
      </div>
    </div>
  );
}
